---
title: "Как написать title и description: ориентиры по длине без потери смысла"
description: "Рабочий способ сократить SEO title и description: сначала сохранить интент, затем проверить диапазон символов и не принимать счётчик за гарантию сниппета."
locale: "ru"
date: "2026-07-23"
tags: ["seo", "title", "description", "редактура"]
relatedTools: ["seoLimits", "textCounter"]
translations:
  en: "how-to-write-title-and-meta-description-without-truncation"
faq:
  - q: "Есть ли у title и description строгий лимит символов?"
    a: "Нет единого жёсткого лимита показа. Поисковая система может обрезать или переписать сниппет в зависимости от запроса, устройства и содержимого страницы. Диапазоны удобны как редакторский ориентир."
  - q: "Какие диапазоны показывает txtify?"
    a: "Текущая проверка использует 45–60 символов для title и 120–160 для description. Это практические диапазоны, а не требование поисковой системы и не гарантия показа."
  - q: "Проверяет ли инструмент ключевые слова и позиции?"
    a: "Нет. Он считает видимые символы и показывает состояние диапазона. Релевантность запросу, качество формулировки и будущие позиции нужно оценивать отдельно."
  - q: "Текст title и description отправляется на сервер?"
    a: "Нет. Подсчёт выполняется локально в браузере; в аналитику передаются только длины полей и состояния диапазонов, но не введённый текст."
---

Не сокращайте title с конца только ради зелёного счётчика. Сначала
сформулируйте, что это за страница и какую задачу она решает, затем уберите
повторы, общие слова и только после этого — бренд. В description сначала
дайте ответ, затем важное условие и действие.

В [проверке SEO-лимитов](/seo-limity) используются такие редакторские
диапазоны:

| Поле | Диапазон в txtify | Что поставить в начало |
| --- | ---: | --- |
| Title | 45–60 символов | Тема страницы и основная задача |
| Description | 120–160 символов | Результат, важное ограничение, следующий шаг |
| Текст сниппета | 140–220 символов | Законченная мысль в первых строках |
| Короткий анонс | 70–110 символов | Смысл без служебного вступления |

Это не технические запреты. Поисковая система может показывать текст разной
ширины, брать фрагмент со страницы или переписывать заголовок под конкретный
запрос. Счётчик нужен как ранний сигнал «слишком коротко» или «хвост уже
рискует исчезнуть», а не как прогноз позиции.

## Сначала соберите title по смыслу

Черновой шаблон:

`[что это] + [для какой задачи или с каким отличием] + [бренд при необходимости]`

Слабый вариант:

`Главная | Полезный онлайн-сервис для работы с документами`

Из него непонятно, какая страница открывается и какую задачу она решает.

Рабочий вариант:

`Сжать PDF до 5 МБ онлайн — проверка размера`

Если хвост обрежется, пользователь всё равно увидит действие, формат и целевой
размер. Самые важные слова стоят до бренда и рекламных уточнений.

При сокращении title убирайте в таком порядке:

1. повтор бренда или категории, уже видимой в домене;
2. слова `лучший`, `удобный`, `современный`, если за ними нет проверяемого
   отличия;
3. второй синоним того же действия;
4. уточнение, которое уже ясно из запроса;
5. бренд в конце, если без него смысл остаётся точным.

Не удаляйте формат, направление конвертации и число, которое меняет задачу.
`PDF в JPG` и `JPG в PDF` — разные страницы, а `до 5 МБ` отличает конкретный
сценарий от общего сжатия.

## Description не должен пересказывать title

Title называет страницу. Description объясняет, что пользователь получит и
какое условие стоит знать до клика.

Удобная конструкция:

`[результат] + [важная граница] + [что сделать]`

Например:

`Уменьшите PDF и сразу проверьте, попал ли файл в лимит 5 МБ. Первый проход
работает в браузере, усиленное сжатие запускается только по выбору.`

Здесь нет гарантии «идеального качества» и нет повторения одного ключа четыре
раза. Зато заранее названы результат и режим обработки.

Если description длинный, сначала удалите:

- приветствия и фразы `на этой странице вы можете`;
- повтор title слово в слово;
- перечисление функций, не важных для запроса;
- призывы `узнайте больше`, после которых не остаётся конкретики;
- второе предложение, если первое уже даёт полный ответ.

## Почему 60 символов не являются магической границей

Одинаковое число символов занимает разную ширину: `Ш` и `i`, цифры и эмодзи
визуально не равны. На телефоне и широком экране доступное место тоже
отличается. Кроме того, поисковик может взять заголовок или фрагмент из
содержимого страницы.

txtify считает графемы — видимые символы ближе к тому, как их воспринимает
человек. Инструмент не измеряет пиксели, не моделирует выдачу и не проверяет
релевантность. Для отдельной сверки слов, строк и UTF-8 размера используйте
[счётчик символов](/schetchik-simvolov).

## Финальная проверка перед публикацией

- В начале title понятна конкретная страница, даже без хвоста.
- Направление операции и важные числа не потеряны.
- Description добавляет результат или ограничение, а не повторяет title.
- Обещания совпадают с тем, что пользователь увидит после клика.
- Оба поля проверены в [SEO-лимитах](/seo-limity), но зелёный статус не
  принят за гарантию показа.
- После индексации сопоставлены реальные запросы, показы и CTR в поисковой
  аналитике: эффект нельзя оценивать только по длине или одному варианту
  сниппета.

Сохраняйте исходный и новый варианты рядом. Если меняете метаданные уже
работающей страницы, оценивать нужно не только длину, но и показы, CTR и
релевантность запросов после переобхода.
